import React, { useState } from "react";
import { Box, Typography, Chip, Button, IconButton } from "@mui/material";
import PetsIcon from "@mui/icons-material/Pets";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import LocalFireDepartmentRoundedIcon from "@mui/icons-material/LocalFireDepartmentRounded";
import ScaleRoundedIcon from "@mui/icons-material/ScaleRounded";
import type { Dog } from "../../types";
import { fmtAge } from "../../utils/format";
import { caloriesFromGrams } from "@/domains/nutrition/FoodEnergy";
import { QuizStyledCard } from "../shared/QuizStyledCard";
import { DeleteDogDialog } from "./DeleteDogDialog";
import { QUIZ } from "@/theme/quizTokens";

function getChips(dog: Dog): { label: string; color: string; bg: string }[] {
  const chips: { label: string; color: string; bg: string }[] = [];

  const activity = String(dog.activityLevel || "").toLowerCase();
  if (activity.includes("90") || activity === "high") {
    chips.push({ label: "High Activity", color: "#2196F3", bg: "#E3F2FD" });
  } else if (activity.includes("0-30") || activity.includes("0–30") || activity === "low") {
    chips.push({ label: "Low Activity", color: "#CE7A00", bg: "#FFF0D6" });
  } else if (activity) {
    chips.push({ label: "Moderate", color: "#2E7D32", bg: "#E5FFEA" });
  }

  const condition = String(dog.bodyCondition || "").toLowerCase();
  if (condition === "ideal") {
    chips.push({ label: "Ideal Weight", color: "#2E7D32", bg: "#E5FFEA" });
  } else if (condition === "underweight") {
    chips.push({ label: "Underweight", color: "#CE7A00", bg: "#FFF0D6" });
  } else if (condition === "overweight") {
    chips.push({ label: "Overweight", color: "#D14C3F", bg: "#FFE5E2" });
  }

  if ((dog.allergies && dog.allergies.length > 0) || (dog.healthIssues && dog.healthIssues.length > 0)) {
    chips.push({ label: "Special Care", color: "#D14C3F", bg: "#FFE5E2" });
  }

  return chips.slice(0, 3);
}

export function DogSummaryCard({
  dog,
  onViewProfile,
  onEdit,
  onDelete,
  onRestore,
  canDelete = true,
}: {
  dog: Dog;
  onViewProfile: (dogId: string) => void;
  onEdit: (dogId: string) => void;
  onDelete?: (dogId: string) => void;
  onRestore?: (dogId: string) => void;
  canDelete?: boolean;
}) {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const isDeleted = !!dog.deleted;
  const chips = getChips(dog);
  const dailyGrams = dog.dailyGrams ?? dog.currentNutrition?.dailyGrams ?? null;
  // Derive from grams so legacy stored calories (pre-rounding MER) aren't shown
  const dailyCalories =
    dailyGrams != null
      ? caloriesFromGrams(dailyGrams)
      : dog.dailyCalories ?? dog.currentNutrition?.dailyCalories ?? null;
  const sexLabel = dog.sex === "MALE" ? "Male" : dog.sex === "FEMALE" ? "Female" : dog.sex || "";
  const subtitle = [dog.breed, sexLabel].filter(Boolean).join(" \u00B7 ");

  return (
    <QuizStyledCard variant="white">
      {/* Remove button - top right corner */}
      {!isDeleted && onDelete && canDelete && (
        <Box sx={{ display: "flex", justifyContent: "flex-end", mb: -1, mr: -1 }}>
          <IconButton
            onClick={(e) => {
              e.stopPropagation();
              setDeleteDialogOpen(true);
            }}
            size="small"
            sx={{
              width: 28,
              height: 28,
              backgroundColor: "#D14C3F",
              color: QUIZ.color.white,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "#B7372B",
                transform: "scale(1.05)",
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>
      )}

      <Box
        onClick={() => !isDeleted && onViewProfile(String(dog.id))}
        sx={{
          cursor: isDeleted ? "default" : "pointer",
          ...(isDeleted ? { opacity: 0.5, filter: "grayscale(60%)" } : {}),
        }}
      >
        {/* Removed badge */}
        {isDeleted && (
          <Box sx={{ mb: 1.5 }}>
            <Chip
              label="Removed"
              size="small"
              sx={{
                height: 22,
                fontSize: 11,
                fontWeight: 800,
                backgroundColor: "#D14C3F",
                color: QUIZ.color.white,
              }}
            />
          </Box>
        )}

        {/* Header: avatar + name + subtitle */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: "14px",
              background: "linear-gradient(135deg, #E9DFD7 0%, #FFD1CC 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <PetsIcon sx={{ fontSize: 24, color: QUIZ.color.brown }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              sx={{
                fontSize: { xs: 18, md: 20 },
                fontWeight: 800,
                color: QUIZ.color.brown,
                lineHeight: 1.2,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {dog.name || "Unnamed Dog"}
            </Typography>
            {subtitle && (
              <Typography
                sx={{
                  fontSize: 13,
                  color: QUIZ.color.brown,
                  opacity: 0.65,
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Age */}
        <Typography
          sx={{ fontSize: 13, fontWeight: 700, color: QUIZ.color.brown, opacity: 0.7, mb: 1 }}
        >
          Age: {fmtAge(dog)}
        </Typography>

        {/* Chips */}
        {chips.length > 0 && (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 1.5 }}>
            {chips.map((chip, i) => (
              <Chip
                key={i}
                label={chip.label}
                size="small"
                sx={{
                  height: 24,
                  fontSize: 11,
                  fontWeight: 700,
                  backgroundColor: chip.bg,
                  color: chip.color,
                }}
              />
            ))}
          </Box>
        )}

        {/* Plan snapshot */}
        {(dailyGrams != null || dailyCalories != null) && (
          <Box
            sx={{
              display: "flex",
              borderRadius: "10px",
              overflow: "hidden",
              mb: 1,
            }}
          >
            {dailyCalories != null && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  py: 1,
                  backgroundColor: QUIZ.color.cream2,
                }}
              >
                <LocalFireDepartmentRoundedIcon sx={{ fontSize: 16, color: QUIZ.color.brown }} />
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: QUIZ.color.brown }}>
                  {Math.round(Number(dailyCalories))} kcal
                </Typography>
              </Box>
            )}
            {dailyGrams != null && (
              <Box
                sx={{
                  flex: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 0.75,
                  py: 1,
                  backgroundColor: "#E5FFEA",
                }}
              >
                <ScaleRoundedIcon sx={{ fontSize: 16, color: "#2E7D32" }} />
                <Typography sx={{ fontSize: 13, fontWeight: 800, color: QUIZ.color.brown }}>
                  {Math.round(Number(dailyGrams))}g/day
                </Typography>
              </Box>
            )}
          </Box>
        )}
      </Box>

      {/* Actions */}
      {!isDeleted ? (
        <Box sx={{ display: "flex", gap: 1, mt: 1.5 }}>
          <Button
            variant="contained"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onViewProfile(String(dog.id));
            }}
            sx={{
              flex: 1,
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 13,
              py: 1,
              bgcolor: QUIZ.color.brown,
              "&:hover": { bgcolor: "#5C2504" },
            }}
          >
            View Profile
          </Button>
          <Button
            variant="outlined"
            size="small"
            startIcon={<EditRoundedIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => {
              e.stopPropagation();
              onEdit(String(dog.id));
            }}
            sx={{
              borderRadius: "10px",
              textTransform: "none",
              fontWeight: 800,
              fontSize: 13,
              py: 1,
              px: 2,
              borderColor: "rgba(74,48,38,0.2)",
              color: QUIZ.color.brown,
              "&:hover": {
                borderColor: QUIZ.color.brown,
                bgcolor: "rgba(255,225,221,0.35)",
              },
            }}
          >
            Edit
          </Button>
        </Box>
      ) : (
        onRestore && (
          <Button
            fullWidth
            variant="contained"
            onClick={() => onRestore(String(dog.id))}
            sx={{
              mt: 1.5,
              py: 1,
              borderRadius: "10px",
              background: "linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)",
              color: QUIZ.color.white,
              fontWeight: 800,
              fontSize: 13,
              textTransform: "none",
              "&:hover": { background: "linear-gradient(135deg, #43A047 0%, #5CB85C 100%)" },
            }}
          >
            Restore {dog.name || "Dog"}
          </Button>
        )
      )}

      <DeleteDogDialog
        open={deleteDialogOpen}
        dogName={dog.name || "this dog"}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={() => {
          setDeleteDialogOpen(false);
          onDelete?.(String(dog.id));
        }}
      />
    </QuizStyledCard>
  );
}
